import { useQuery } from '@tanstack/react-query';
import { Badge, Button, InlineError, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { jobSteps } from '../cgOrders/jobStages';
import { slaRemaining, slaTone, todayNow } from '../cgOrders/sla';
import { StageTimeline } from '../cgOrders/StageTimeline';
import {
  currentProofs,
  fetchJobOrder,
  fetchSupplierPoDocuments,
  fetchSupplierStageEvents,
  whatToDoNow,
} from './jobOrder';
import { PoHistory } from '../cgOrders/PoHistory';
import { TrackingValue } from '../cgOrders/TrackingValue';
import { trackingLabel } from '../cgOrders/tracking.link';
import { ProofImage } from '../cgOrders/ProofImage';
import { ArtworkDownload } from '../cgOrders/ArtworkDownload';
import { SampleApproval } from './SampleApproval';
import { SupplierSampleSlots } from './SupplierSampleSlots';
import { useMimic } from '../supplierOrganizations/MimicMode';
import type { JobOrder } from './jobOrder';
import { JobStageBar } from './JobStageBar';
import { JobOrderActions } from './JobOrderActions';
import { useEffectiveSupplierOrg } from '../supplierOrganizations/MimicMode';

/**
 * One job order, everything about it, on one page.
 *
 * ── What this replaces ─────────────────────────────────────────────────────
 *
 * The portal filed a factory's work by SYSTEM — Specs & Proofing, Messages,
 * Production & Logistics — which are our internal categories. A factory does
 * not have a handoff and an order and a submission run. It has one job, and
 * that job was scattered across three tabs and two record types.
 *
 * Worse, the one thing they most needed was on none of them: what they were
 * being asked to build. A title, some quantities and an Accept button. The
 * client-approved artwork existed the whole time, snapshotted the moment the
 * PO went out, and was never shown to the people making it.
 *
 * So this page leads with the instruction, then the PO, then the artwork —
 * everything needed to decide whether to accept, before the button to accept.
 */
export function JobOrderPanel({ orderId, onBack }: { orderId: string; onBack: () => void }) {
  const { client } = useAuth();
  const organizationId = useEffectiveSupplierOrg();
  const job = useQuery({
    queryKey: ['supplier-job-order', orderId],
    queryFn: () => fetchJobOrder(client, orderId),
    retry: false,
  });
  const pos = useQuery({
    queryKey: ['supplier-po-documents', orderId],
    queryFn: () => fetchSupplierPoDocuments(client, orderId),
    retry: false,
  });
  const stages = useQuery({
    queryKey: ['supplier-stage-events', orderId],
    queryFn: () => fetchSupplierStageEvents(client, orderId),
    retry: false,
  });
  if (job.isPending) {
    return <Skeleton lines={6} label="Loading job order" />;
  }
  if (job.isError || job.data === null || job.data === undefined) {
    return (
      <div>
        <Button variant="secondary" size="sm" onClick={onBack}>
          ← All jobs
        </Button>
        <InlineError>
          This job order could not be opened. It may have been reassigned to another supplier.
        </InlineError>
      </div>
    );
  }

  const data = job.data;
  // One derivation, shared with TAP's board. The payload satisfies
  // JobStageInput structurally, so this needs no cast — which is the point of
  // that type being three fields rather than a whole order row.
  const steps = jobSteps(data, { sampleSubmitted: data.sample_photo_count > 0 });
  // Whichever promise is live. Two clocks, but only one is ever running: the
  // sample window closes when the samples are approved, which is the moment
  // the production window opens. Showing both would put a settled deadline
  // beside a live one and leave the factory to work out which is theirs now.
  const sla =
    data.production_clock_started_at === null
      ? slaRemaining(data.sample_clock_started_at, data.sample_due_days, todayNow())
      : slaRemaining(data.production_clock_started_at, data.production_due_days, todayNow());
  const proofs = currentProofs(data.proofs);

  return (
    // A bounded panel, not a section on a page. It used to render straight
    // onto the tab with no edge of its own, so the job ran into whatever was
    // below it and there was no telling where one ended and the other began.
    // A box rather than a modal: this is the screen a factory works from all
    // day, and trapping them in a dialog to read a spec is the wrong trade.
    <div className="job-order job-order--panel">
      <div className="job-order-top">
        <Button variant="secondary" size="sm" onClick={onBack}>
          ← All jobs
        </Button>
      </div>

      <div className="job-order-head">
        <h3 className="job-order-no">Job Order {data.order_no}</h3>
        <Badge tone="neutral">{data.status}</Badge>
        {data.supplier_confirmed_at === null ? null : (
          <Badge tone={slaTone(sla.state)}>{sla.label}</Badge>
        )}
      </div>

      <JobStageBar steps={steps} viewer="supplier" />

      {/* The instruction, before anything else. One sentence, and on the
          accept step it points at the artwork rather than at the button. */}
      <p className="job-order-todo">{whatToDoNow(data)}</p>

      {data.changes_requested.length === 0 ? null : (
        <p className="job-order-changes">
          <strong>Client asked for changes:</strong> {data.changes_requested.join(', ')}
        </p>
      )}

      <JobOrderActions
        job={data}
        organizationId={organizationId}
        onDone={() => void job.refetch()}
      />

      <JobPo job={data} />

      {/* No Client approval section, deliberately. The certificate is TAP's
          document about TAP's client — signatory, title, email — and even its
          number is a handle for asking after it. What a factory needs is the
          approved artwork itself, which is below, frozen on the PO. Kyu:
          "only TAP should see and access that."
          20261114000100 closed the RPC; this is the other half. */}
      <section className="job-order-section">
        <h4>Time in each stage</h4>
        <StageTimeline events={stages.data ?? []} today={todayNow()} />
      </section>

      <section className="job-order-section">
        <h4>Purchase orders</h4>
        {/* The document itself, open. The banner above says "Read the PO" and
            this used to be a bare revision-number link — the person told to
            read it could not find it. */}
        <PoHistory
          documents={pos.data ?? []}
          isPending={pos.isPending}
          isError={pos.isError}
          showInForce
        />
      </section>

      {/* The artwork. This is the part that has never been on their screen. */}
      <section className="job-order-section">
        <h4>Approved artwork</h4>
        {proofs.length === 0 ? (
          <p className="job-order-muted">
            No approved artwork on this job yet. Ask TAP before starting.
          </p>
        ) : (
          <>
            <p className="job-order-muted">
              What the client signed off. Build to these — the newest version of each component is
              shown.
            </p>
            <ul className="job-order-proofs">
              {proofs.map((proof) => (
                <li key={`${proof.component}-${String(proof.version)}`}>
                  <ProofImage url={proof.url} alt={proof.component} />
                  <span className="job-order-proof-name">
                    {proof.component} <span className="job-order-muted">v{proof.version}</span>
                  </span>
                  {/* The file itself, on their machine. A factory prints
                      these and pins them at a workstation; a thumbnail they
                      can only look at in a browser is not artwork they can
                      build from. Falls back to opening the file when no
                      direct-download form of the URL exists. */}
                  <ArtworkDownload url={proof.url} component={proof.component} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* Kyu, 2 Sep: "whenever the supplier sees the other images, they
          should see this as well." The client's logo, beside the artwork but
          under its own heading — nobody approved it, and the sheet must not
          suggest somebody did. */}
      {(data.reference_files ?? []).length === 0 ? null : (
        <section className="job-order-section">
          <h4>Reference files</h4>
          <p className="job-order-muted">
            Supplied by TAP for this job. Not part of the client's approval.
          </p>
          <ul className="job-order-proofs">
            {(data.reference_files ?? []).map((file) => (
              <li key={`${file.kind}-${String(file.version)}`}>
                <ProofImage url={file.url} alt={referenceLabel(file)} />
                <span className="job-order-proof-name">
                  {referenceLabel(file)} <span className="job-order-muted">v{file.version}</span>
                </span>
                <ArtworkDownload url={file.url} component={referenceLabel(file)} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <JobSamples job={data} />
    </div>
  );
}

/**
 * The client logo's kind says what it is; the two extra slots carry a name the
 * uploader typed. That name is the whole point — a file arriving as "extra_1"
 * is no more use to the factory than one arriving on WhatsApp with no note.
 */
function referenceLabel(file: { kind: string; label?: string }): string {
  if (file.kind === 'client_logo') return 'Client logo';
  const label = (file.label ?? '').trim();
  return label === '' ? file.kind : label;
}

/**
 * The PO, readable before accepting.
 *
 * Carries no money of any kind — not filtered here, absent from the payload.
 * The certificate line proves the design is locked without naming the person
 * who signed it: that is TAP's client, and their identity is a commercial
 * relationship rather than a production detail.
 */
function JobPo({ job }: { job: JobOrder }) {
  return (
    <section className="job-order-section">
      <h4>The PO</h4>
      <ul className="job-order-lines">
        {job.items.map((line, index) => (
          <li key={`${line.sku}-${String(index)}`}>
            <strong>
              {line.units} × {line.name}
            </strong>
            {line.sku === '' ? '' : ` · ${line.sku}`}
            {line.spec === '' ? '' : ` · ${line.spec}`}
          </li>
        ))}
      </ul>
      <dl className="job-order-facts">
        {job.target_ship_date === null ? null : (
          <>
            <dt>Target ship date</dt>
            <dd>{job.target_ship_date}</dd>
          </>
        )}
        {job.ship_to.trim() === '' ? null : (
          <>
            <dt>Ship to</dt>
            <dd>{job.ship_to}</dd>
          </>
        )}
        {trackingLabel(job.courier_name, job.tracking_number) === '' ? null : (
          <>
            <dt>Tracking</dt>
            <dd>
              <TrackingValue
                courier={job.courier_name}
                tracking={job.tracking_number}
                url={job.tracking_url}
              />
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

/** Their own samples and what TAP said about each, on the job rather than in a list beside it. */
/**
 * The factory's samples, WORKED ON here rather than viewed here.
 *
 * Kyu: "the Sample Submission is not within the job order, it's outside,
 * under Inventory > New Submission. We need to unify this and put that
 * functionality under the same job order, in a section below. The same
 * philosophy as the order page profile in our side."
 *
 * This section used to render its own read-only list of the photos, and the
 * actual UPLOAD lived on the Inventory tab — so a factory read "make the
 * pre-production samples and upload a photo of each component" on this page
 * and then had to leave it to comply. The job page told them what to do and
 * the doing was filed under stock.
 *
 * Now the run mounts the SAME component slots the Inventory tab uses —
 * SupplierSampleSlots, one implementation, exactly as the review side shares
 * one — with uploads open on the same rule PortalExchange applies: the run is
 * still submitted or a revision was asked, and preview mode has writes on.
 * The Inventory tab keeps working; nothing was moved, the capability was put
 * where the instruction is.
 */
function JobSamples({ job }: { job: JobOrder }) {
  const { canWrite } = useMimic();
  return (
    <section className="job-order-section">
      <h4>Your samples</h4>
      {job.samples.length === 0 ? (
        // Before acceptance there is no run to upload into — one is opened
        // automatically the moment the factory accepts the PO. Saying so
        // beats an empty box that reads as broken.
        <p className="job-order-muted">
          A sample run opens here automatically when you accept the PO.
        </p>
      ) : (
        job.samples.map((run) => (
          <div key={run.submission_id} className="job-order-run">
            <div className="job-order-run-head">
              <strong>{run.title}</strong>
              <span className="job-order-muted">{run.created_at.slice(0, 10)}</span>
            </div>
            {/* supplier_job_order carries the run's status but not its
                decision date, so the stamp here is undated; the portal's
                Completed list has the date. */}
            <SampleApproval status={run.status} decidedBy={null} decidedAt={null}>
              <SupplierSampleSlots
                submissionId={run.submission_id}
                canUpload={
                  canWrite && (run.status === 'submitted' || run.status === 'revision_requested')
                }
              />
            </SampleApproval>
          </div>
        ))
      )}
    </section>
  );
}
